import { CaretDown, CircleNotch, type Icon, Plus } from "@phosphor-icons/react";
import clsx from "clsx";
import {
  type ButtonHTMLAttributes,
  type ForwardedRef,
  type FunctionComponent,
  forwardRef,
  type ReactNode,
  type SVGProps,
} from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import { getWhitelabelCustomization } from "common/helpers/utils";

import { type ButtonVariantType, buttonVariants } from "../ui/Button";

import type { VariantProps } from "class-variance-authority";

type SplitButtonVariant = Extract<
  ButtonVariantType,
  | "default-black"
  | "default-purple"
  | "secondary-black"
  | "secondary-purple"
  | "outline-black"
>;

type SplitButtonDropdownMenuItem = {
  label: string;
  icon: FunctionComponent<SVGProps<SVGSVGElement>> | Icon;
  onSelect: () => void;
  disabled?: boolean;
  variant?: "default" | "danger" | "blue";
};

/**
 * The right half is either a dropdown trigger or a plain add button, never
 * both. `onClick` stays the main button's handler, the add button is driven by
 * `onSplitButtonClick`.
 */
export type SplitButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<VariantProps<typeof buttonVariants>, "intent" | "variant"> & {
    variant?: SplitButtonVariant;
    leftIcon?: ReactNode;
    isLoading?: boolean;
  } & (
    | {
        dropdownMenuItems: SplitButtonDropdownMenuItem[];
        onSplitButtonClick?: never;
        splitButtonAriaLabel?: never;
      }
    | {
        onSplitButtonClick: () => void;
        splitButtonAriaLabel: string;
        dropdownMenuItems?: never;
      }
  );

const dividerColorClass: Record<SplitButtonVariant, string> = {
  "default-black": "before:bg-black-700",
  "default-purple": "before:bg-purple-400",
  "secondary-black": "before:bg-black-200",
  "secondary-purple": "before:bg-purple-200",
  "outline-black": "before:bg-black-200",
};

function SplitButton(
  {
    className,
    variant = "default-black",
    size = "md",
    children,
    leftIcon = null,
    disabled,
    isLoading = false,
    dropdownMenuItems,
    onSplitButtonClick,
    splitButtonAriaLabel,
    ...props
  }: SplitButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  if (getWhitelabelCustomization() && variant === "secondary-purple") {
    variant = "secondary-black";
  }

  const loadingComponent = <CircleNotch className="animate-spin" />;

  //shared by the dropdown trigger and the add button so both halves stay aligned
  const rightElementClass = buttonVariants({
    variant,
    size,
    intent: "iconOnly",
    hasWhitelabelColor: !!getWhitelabelCustomization()?.primary_color,
    className: [
      "rounded-l-none relative",
      "before:absolute before:left-0 before:h-5 before:w-px disabled:before:bg-black-400",
      dividerColorClass[variant],
      variant === "outline-black" && "border-l-0",
    ],
  });

  return (
    <div className={clsx("flex", className)}>
      <button
        type="button"
        className={buttonVariants({
          variant,
          size,
          intent: "default",
          hasLeftIcon: !!leftIcon,
          hasRightIcon: true,
          hasWhitelabelColor: !!getWhitelabelCustomization()?.primary_color,
          className: clsx(
            "rounded-r-none flex-1",
            variant === "outline-black" && "border-r-0",
          ),
        })}
        ref={ref}
        {...props}
        disabled={isLoading || disabled}
      >
        {isLoading && leftIcon ? loadingComponent : leftIcon}
        {children}
      </button>

      {dropdownMenuItems ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            className={clsx(rightElementClass, "group/dropdown-button")}
            disabled={isLoading || disabled}
          >
            <CaretDown className="transition-transform group-data-[state=open]/dropdown-button:rotate-180" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuList>
              {dropdownMenuItems.map(
                ({ label, icon: Icon, onSelect, disabled, variant }) => (
                  <DropdownMenuItem
                    onSelect={onSelect}
                    key={label}
                    disabled={disabled}
                    variant={variant}
                  >
                    <Icon />
                    {label}
                  </DropdownMenuItem>
                ),
              )}
            </DropdownMenuList>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <button
          type="button"
          className={rightElementClass}
          disabled={isLoading || disabled}
          onClick={onSplitButtonClick}
          aria-label={splitButtonAriaLabel}
        >
          <Plus />
        </button>
      )}
    </div>
  );
}

SplitButton.displayName = "SplitButton";

const SplitButtonWithRef = forwardRef(SplitButton);

export { SplitButtonWithRef as SplitButton };
